/* eslint-disable react-hooks/exhaustive-deps */
/**
 * @fileoverview Wrapper UI para mostrar el buscador de respuestas rápidas (FastAnswersShortcutMC) al escribir '/' en el textarea del chat.
 * Detecta el trigger y posiciona el componente de sugerencias sobre el input.
 * Cumple Atomic Design y separación estricta de UI Layer.
 *
 * @module FastAnswersShortcutWrapperMC
 * @category Component - Molecule
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, trigger '/' y posicionamiento absoluto
 *
 * @typedef {Object} FastAnswersShortcutWrapperMCProps
 * @property {React.RefObject} textareaRef - Ref del textarea donde se escribe el mensaje
 * @property {Object} customer - Objeto cliente activo (debe tener id)
 * @property {Object} conversation - Objeto conversación activa (debe tener platform)
 *
 * @component FastAnswersShortcutWrapperMC
 * @category Component - Molecule
 *
 * @description
 * **Características:**
 * - Detecta cuando el usuario escribe '/' en el textarea y muestra sugerencias de respuestas rápidas
 * - Posiciona el componente FastAnswersShortcutMC sobre el input
 * - Oculta el buscador si no hay trigger
 * - Cumple Atomic Design: Molécula, reutilizable en cualquier input de chat
 * - Integración con FastAnswersShortcutMC
 *
 * **Atomic Design:**
 * - **Nivel:** Molecule
 * - **Composición:** Usa FastAnswersShortcutMC
 * - **Dependencias:** useState, useEffect
 *
 * **Props Interface:**
 * ```javascript
 * {
 *   textareaRef: React.RefObject, // Ref del textarea
 *   customer: Object,             // Cliente activo
 *   conversation: Object          // Conversación activa
 * }
 * ```
 *
 * @param {FastAnswersShortcutWrapperMCProps} props - Propiedades del componente
 * @returns {JSX.Element|null} Wrapper renderizado o null si no aplica
 *
 * @example
 * <FastAnswersShortcutWrapperMC textareaRef={ref} customer={customer} conversation={conversation} />
 *
 * @see {@link ../../FastAnswers/FastAnswersShortcutMC.js} Componente de sugerencias
 *
 * @since 2.0.0
 * @version 3.2.0 - JSDoc completo, trigger '/' y posicionamiento absoluto
 */
import React, { useEffect, useState } from "react";
import FastAnswersShortcutMC from "../../FastAnswers/FastAnswersShortcutMC";
import useSelectedConversation from '../../../hooks/shared/useSelectedConversation';

const FastAnswersShortcutWrapperMC = ({ textareaRef }) => {
  const [show, setShow] = useState(false);
  const [text, setText] = useState("");

  const conversation = useSelectedConversation();

  useEffect(() => {
    if (!textareaRef?.current) return;

    const handleInput = () => {
      const { value } = textareaRef.current;
      setShow(value.startsWith('/'));
      setText(value);
    };

    textareaRef.current?.addEventListener('input', handleInput);

    // eslint-disable-next-line consistent-return
    return () => {
      textareaRef.current?.removeEventListener('input', handleInput);
    };
  }, [textareaRef]);

  return (
    show ? (
      <div className="tw-absolute tw-bottom-4 tw-z-50 tw-pointer-events-none">
        <div className="tw-pointer-events-auto">
          <FastAnswersShortcutMC
            searchTerm={text.replace(/^\//, "")}
            customer={conversation?.customerId}
            platform={conversation?.platform}
          />
        </div>
      </div>
    ) : null
  );
};

export default FastAnswersShortcutWrapperMC;
